import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { formatEuro, formatPercent, formatNumber } from '../../utils/currency';
import { formatDate, formatShortDate } from '../../utils/date';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { getStocks, StockDto } from '../../services/stocksApi';
import { computeHoldings, aggregatePortfolio, HoldingAnalysis, PortfolioAggregate } from './analysis/portfolioAnalytics';
import { buildPerformance, PortfolioPerformanceSummary } from './analysis/performanceAnalytics';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';
// Lazy loaded heavy chart primitives (after static imports per import/first rule)
const AreaChart = React.lazy(() => import('recharts').then(m => ({ default: m.AreaChart })));
const Area = React.lazy(() => import('recharts').then(m => ({ default: m.Area })));
const Line = React.lazy(() => import('recharts').then(m => ({ default: m.Line })));
const BarChart = React.lazy(() => import('recharts').then(m => ({ default: m.BarChart })));
const Bar = React.lazy(() => import('recharts').then(m => ({ default: m.Bar })));

interface PerformanceChartPoint {
  date: string; // YYYY-MM-DD (month end)
  portfolioValue: number;
  invested: number;
  cumulativeDividends: number;
  monthlyReturn: number;
}

interface StockPerformance {
  symbol: string;
  totalReturn: number;
  totalReturnPercentage: number;
  annualizedReturn: number;
  volatility: number;
  sharpeRatio: number;
  maxDrawdown: number;
  beta: number; // vs S&P 500
}

const PerformancePage: React.FC = () => {
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<'6M' | '1Y' | '2Y' | '5Y' | 'ALL'>('1Y');

  useEffect(() => {
    loadPerformanceData();
  }, []);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allStocks = await getStocks();
      setStocks(allStocks);
      
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError('Não foi possível carregar os dados de desempenho');
    } finally {
      setLoading(false);
    }
  };

  // Deterministic portfolio performance series derived from actual purchases, sales, dividends
  const performanceSummary: PortfolioPerformanceSummary | null = useMemo(() => {
    if (!stocks.length) return null;
    return buildPerformance(stocks, timeframe);
  }, [stocks, timeframe]);

  const performanceHistory: PerformanceChartPoint[] = useMemo(() => {
    if(!performanceSummary) return [];
    return performanceSummary.points.map(p => ({
      date: p.date,
      portfolioValue: p.value,
      invested: p.invested,
      cumulativeDividends: p.cumulativeDividends,
      monthlyReturn: p.monthlyReturnPct
    }));
  }, [performanceSummary]);

  const holdings: HoldingAnalysis[] = useMemo(()=> computeHoldings(stocks), [stocks]);
  const aggregate: PortfolioAggregate = useMemo(()=> aggregatePortfolio(holdings), [holdings]);
  // Stable random-like metrics cached per symbol so UI does not flicker on re-renders
  const metricCacheRef = useRef<Map<string, { volatility:number; maxDrawdown:number; beta:number }>>(new Map());
  const stockPerformance = useMemo((): StockPerformance[] => {
    return holdings.map(h => {
      const totalReturn = h.totalReturn;
      const totalReturnPercentage = h.totalReturnPct;
      const annualizedReturn = totalReturnPercentage; // placeholder until real history
      let cached = metricCacheRef.current.get(h.symbol);
      if(!cached){
        cached = {
          volatility: Math.random()*30 + 10,
          maxDrawdown: -(Math.random()*20 + 5),
          beta: 0.5 + Math.random()
        };
        metricCacheRef.current.set(h.symbol, cached);
      }
      const sharpeRatio = cached.volatility !== 0 ? annualizedReturn / cached.volatility : 0;
      return {
        symbol: h.symbol,
        totalReturn,
        totalReturnPercentage,
        annualizedReturn,
        volatility: cached.volatility,
        sharpeRatio,
        maxDrawdown: cached.maxDrawdown,
        beta: cached.beta
      };
    }).sort((a,b)=> b.totalReturnPercentage - a.totalReturnPercentage);
  }, [holdings]);

  const portfolioMetrics = useMemo(() => {
    const summary = performanceSummary;
    const totalInvested = aggregate.totalInvested;
    const totalCurrentValue = aggregate.totalValue;
    const totalReturn = aggregate.totalReturn;
    const totalReturnPercentage = aggregate.totalReturnPct;
    const sharpe = summary ? summary.sharpeRatio : 0;
    const vol = summary ? summary.volatilityAnnualPct : 0;
    const maxDD = summary ? summary.maxDrawdownPct : 0;
    const annualized = summary ? summary.annualizedReturnPct : totalReturnPercentage;
    const benchmarkReturn = 8; // simple static benchmark assumption
    const alpha = annualized - benchmarkReturn;
    return {
      totalInvested,
      totalCurrentValue,
      totalReturn,
      totalReturnPercentage,
      annualizedReturn: annualized,
      alpha,
      sharpeRatio: sharpe,
      volatilityAnnualPct: vol,
      maxDrawdownPct: maxDD
    };
  }, [aggregate, performanceSummary]);

  const formatCurrency = (amount: number): string => formatEuro(amount);
  const formatPercentage = (percentage: number): string => formatPercent(percentage);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Desempenho da carteira"
        subtitle="Acompanhe o desempenho dos seus investimentos ao longo do tempo com análises detalhadas"
        actions={
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={timeframe}
              label="Período"
              onChange={(e) => setTimeframe(e.target.value as any)}
            >
              <MenuItem value="6M">6 meses</MenuItem>
              <MenuItem value="1Y">1 ano</MenuItem>
              <MenuItem value="2Y">2 anos</MenuItem>
              <MenuItem value="5Y">5 anos</MenuItem>
              <MenuItem value="ALL">Tudo</MenuItem>
            </Select>
          </FormControl>
        }
      />

      {/* Performance Summary Cards */}
      <AutoGrid min={240} gap={3} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TimelineIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Rendimento total
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Box>
                <Typography variant="h5" component="div">
                  {formatCurrency(portfolioMetrics.totalReturn)}
                </Typography>
                <Typography 
                  variant="caption" 
                  color={portfolioMetrics.totalReturnPercentage >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatPercentage(portfolioMetrics.totalReturnPercentage)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Alfa vs S&P 500
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Box>
                <Typography 
                  variant="h5" 
                  component="div"
                  color={portfolioMetrics.alpha >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatPercentage(portfolioMetrics.alpha)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Acima do índice de referência
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Rácio de Sharpe
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Box>
                <Typography variant="h5" component="div">
                  {formatNumber(portfolioMetrics.sharpeRatio)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Rendimento ajustado ao risco
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Performance Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Valor da carteira ao longo do tempo (calculado a partir das transações)
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={400} />
        ) : (
          <Box sx={{ height: 400 }}>
            <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceHistory} data-key="date">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v) => formatShortDate(String(v))} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(Number(value)), name]}
                    labelFormatter={(label) => formatDate(String(label))}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="portfolioValue" 
                    name="Valor da carteira"
                    stackId="1" 
                    stroke="#1976d2" 
                    fill="#1976d2" 
                    fillOpacity={0.6}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="invested" 
                    name="Custo investido"
                    stroke="#ff9800" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulativeDividends"
                    name="Dividendos acumulados"
                    stroke="#4caf50"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Suspense>
          </Box>
        )}
      </Paper>

      {/* Returns Distribution */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Distribuição dos rendimentos mensais (aproximação ponderada pelo tempo)
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <Box sx={{ height: 300 }}>
            <Suspense fallback={<Skeleton variant="rectangular" height={300} />}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(v) => formatShortDate(String(v))} />
                  <YAxis />
                  <Tooltip formatter={(value) => `${formatNumber(Number(value))}%`} labelFormatter={(label)=>`Mês: ${formatDate(String(label))}`} />
                  <Bar dataKey="monthlyReturn" name="Rendimento mensal %" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Suspense>
          </Box>
        )}
      </Paper>

      {/* Individual Stock Performance */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Desempenho por ação
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Símbolo</TableCell>
                <TableCell align="right">Rendimento total</TableCell>
                <TableCell align="right">Rendimento anualizado</TableCell>
                <TableCell align="right">Volatilidade</TableCell>
                <TableCell align="right">Rácio de Sharpe</TableCell>
                <TableCell align="right">Perda máxima</TableCell>
                <TableCell align="right">Beta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : stockPerformance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Sem dados de desempenho
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stockPerformance.map((stock) => (
                  <TableRow key={stock.symbol} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {stock.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {stock.totalReturnPercentage >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          color={stock.totalReturnPercentage >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {formatPercentage(stock.totalReturnPercentage)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatPercentage(stock.annualizedReturn)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${formatNumber(stock.volatility, 1)}%`}
                        size="small"
                        color={stock.volatility < 20 ? 'success' : stock.volatility < 30 ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(stock.sharpeRatio)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color="error.main"
                      >
                        {formatPercentage(stock.maxDrawdown)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={formatNumber(stock.beta)}
                        size="small"
                        color={stock.beta < 1 ? 'primary' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PerformancePage;
